"use client";
import Link from "next/link";
import classnames from "classnames";
import DOMPurify from "@/app/[lang]/utils/dompurify";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";

import styles from "./Hero.module.scss";
import type { HeroProps } from "./interface";

// Helpers
import {getSafeHeading, getStrapiMedia} from "@/app/[lang]/utils/api-helpers";
import { getImageScalingClassname } from "@/app/[lang]/utils/getImageScalingClassname";
import { optimizedSrc, optimizedSrcSet } from "@/app/[lang]/utils/optimized-image-src";

// Components
import Button from "@/app/[lang]/components/ui/Button";
import VideoEmbed from "@/app/[lang]/components/VideoEmbed";
import type { ButtonProps } from "@/app/[lang]/components/ui/Button/interface";
import { AppStoreButton } from "@/app/[lang]/components/AppStoreButton";
import { GooglePlayButton } from "@/app/[lang]/components/GooglePlayButton";

const Hero: React.FC<HeroProps & { isLcp?: boolean }> = ( props ) => {
  const { data } = props;

  const {
    size,
    backgroundColor,
    textColor,
    alignment,
    buttons,
    mobileAlignment,
    showMobileDescription,
    hidden,
    headingLevel
  } = data;

  const heroClassnames = classnames(
    styles.wrapper,
    size && styles[size],
    alignment && styles[alignment],
    `bg-${ backgroundColor}`,
    `text-${ textColor }`,
    mobileAlignment && styles[mobileAlignment]
  );

  const TitleColTag = getSafeHeading(headingLevel, "div")

  if ( !!hidden ) {
    return null;
  }

  return (
    <section className={heroClassnames}>
      <div className={styles.content}>
        <div className="container">
          <TitleColTag
              className={ classnames( styles.h2Style ) }
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.title) }}
          />

          <div
            className={ classnames( styles.description, {
              'hide-on-mobile': ! showMobileDescription
            } ) }
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(data.description),
            }}
          />

          {buttons && (
            <div className={ styles.buttons }>
              { buttons.map((button: ButtonProps["attributes"]) => {
                const key = `${button.type ?? "button"}-${button.text ?? ""}-${button.url ?? ""}`;
                return <HeroButton key={ key } { ...button } />;
              })}
            </div>
          )}
        </div>
      </div>
      {data.mobilePicture?.data || data.picture?.data ?
        <HeroMedia { ...props } /> :
        <HeroVideoEmbed
            media_embedded={data.media_embedded}
            mobile_media_embedded={data.mobile_media_embedded}
        />
      }
    </section>
  );
}

const HeroButton:React.FC<ButtonProps["attributes"]> = ( props ) => {
  const { type, url, hidden } = props;

  if ( !!hidden ) {
    return null;
  }

  if ( type === "applestore" ) {
    return (
      <Link href={url ?? ""} className={styles.buttonStore}>
        <AppStoreButton />
      </Link>
    )
  }

  if ( type === "playstore" ) {
    return (
      <Link href={url ?? ""} className={styles.buttonStore}>
        <GooglePlayButton />
      </Link>
    )
  }

  return <Button attributes={ props } className={styles.buttonCTA}/>
}

const HeroMedia:React.FC<HeroProps & { isLcp?: boolean }> = (props) => {
  const { data } = props;
  const isLcp = props.isLcp === true;
  const { imageScaling } = data;

  // Render server-side (no client/useMediaQuery gate) so the LCP image is in the
  // initial HTML and the browser's preload scanner can fetch it immediately.
  // A native <picture> handles the mobile/desktop art direction without JS and
  // downloads only the source matching the viewport.
  const desktopPicture = data.picture?.data ?? data.mobilePicture?.data;
  const mobilePicture = data.mobilePicture?.data ?? data.picture?.data;

  if ( ! desktopPicture && ! mobilePicture ) {
    return null;
  }

  const desktopSrc = getStrapiMedia(desktopPicture?.attributes?.url);
  const mobileSrc = getStrapiMedia(mobilePicture?.attributes?.url);
  const imgSrc = mobileSrc ?? desktopSrc;

  if ( ! imgSrc ) {
    return null;
  }

  const primaryPicture = mobilePicture ?? desktopPicture;
  const alt = primaryPicture?.attributes?.alternativeText || "PPC Blue Romania";
  const mime = primaryPicture?.attributes?.mime;

  if ( mime?.startsWith("video") ) {
    return (
      <div className={ styles.visual }>
        <video controls width="100%">
          <source src={ imgSrc } type={ mime } />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  return (
    <div className={ styles.visual }>
      <picture>
        { desktopSrc && (
          <source
            media="(min-width: 900px)"
            srcSet={ optimizedSrcSet(desktopSrc, [1024, 1280, 1920]) }
            sizes="50vw"
            width={ desktopPicture?.attributes?.width }
            height={ desktopPicture?.attributes?.height }
          />
        )}
        <img
          className={ getImageScalingClassname(imageScaling) }
          src={ optimizedSrc(imgSrc, 768) }
          srcSet={ optimizedSrcSet(imgSrc, [640, 768, 1024]) }
          sizes="100vw"
          // Intrinsic size of the source that backs this <img> (the mobile
          // asset when there is one). The visual box gets its height from
          // .visual's aspect-ratio, but the attributes keep the intrinsic
          // ratio declared so nothing reflows if that CSS ever changes.
          width={ primaryPicture?.attributes?.width }
          height={ primaryPicture?.attributes?.height }
          alt={ alt }
          fetchPriority={ isLcp ? "high" : "auto" }
          loading={ isLcp ? "eager" : "lazy" }
          decoding="async"
        />
      </picture>
    </div>
  )
}

const HeroVideoEmbed = ({ media_embedded, mobile_media_embedded } : any ) => {
  const [client, setClient] = useState(false);
  const isLarge = useMediaQuery({ minWidth: 900 });

  useEffect(() => {
    setClient(true)
  }, [])

  const url = useMemo(() => {

    if ( !client ) {
      return null;
    }

    if ( isLarge ) {
      return media_embedded ?? mobile_media_embedded;
    }

    return mobile_media_embedded ?? media_embedded

  }, [mobile_media_embedded, media_embedded, isLarge, client]);

  return ( <VideoEmbed url={url} /> )
}

export default Hero
